import _ from 'lodash';
import { createSelector, createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
	Beneficiary,
	beneficiaryIntialState,
	beneficiarySteps,
	beneficiaryStepsStatus,
	beneficiaryType,
	BeneficiaryDetailsCompany,
	BeneficiaryDetailsLocal,
	BeneficiaryDetailsBankAccount,
	BeneficiaryId,
	paymentsMethodsList,
	paymentsMethodsDisplayList,
	paymentsMethodType,
	PendingBeneficiaryStatus,
	BeneficiaryStatus,
	paymentsMethodsMapping,
	paymentsMethodsBeneficiaryProfile,
	BeneficiaryDetailsCompanyForDisplay,
	PaymentCategoryOption,
} from 'constants/beneficiary.constants';
import { formValue, intialMessage, userMessage } from 'constants/ui.constants';
import { transformBeneficiaryCategories } from 'services/beneficiary.services';

interface step {
	status: beneficiaryStepsStatus;
}

type steps = {
	[key in beneficiarySteps]: step;
};

type obj = { [key: string]: string };

interface beneficiaryState {
	items: Beneficiary[];
	loading: boolean;
	loadingBeneficiaries: boolean;
	startLoadingBeneficiaries: boolean;
	message: userMessage;
	upsertingProcess: boolean;
	upsertedBeneficiary: Beneficiary;
	activeStep: beneficiarySteps;
	steps: Partial<steps>;
	paymentCategoryOptions: PaymentCategoryOption[];
}

const intialStep = { status: beneficiaryStepsStatus.notStarted };

const intialSteps = () => {
	let s: Partial<steps> = {};
	_.values(beneficiarySteps).forEach((k) => {
		s[k] = intialStep;
	});
	return s;
};

const initialState: beneficiaryState = {
	items: [],
	loading: false,
	loadingBeneficiaries: false,
	startLoadingBeneficiaries: false,
	message: intialMessage,
	upsertingProcess: false,
	upsertedBeneficiary: beneficiaryIntialState,
	activeStep: beneficiarySteps.BENEFICIARY,
	steps: intialSteps(),
	paymentCategoryOptions: [],
};

export const beneficiarySlice = createSlice({
	name: 'beneficiary',
	initialState,
	reducers: {
		setLoading: (state, action: PayloadAction<boolean>) => {
			state.loading = action.payload;
		},
		setLoadingBeneficiaries: (state, action: PayloadAction<boolean>) => {
			state.loadingBeneficiaries = action.payload;
		},
		setStartLoadingBeneficiaries: (state, action: PayloadAction<boolean>) => {
			state.startLoadingBeneficiaries = action.payload;
		},
		setMessage: (state, action: PayloadAction<userMessage>) => {
			state.message = action.payload;
		},
		toggleUpserting: (state, action: PayloadAction<boolean>) => {
			state.upsertingProcess = action.payload;
			state.upsertedBeneficiary = beneficiaryIntialState;
			state.steps = intialSteps();
			state.activeStep = beneficiarySteps.BENEFICIARY;
			state.message = intialMessage;
		},
		intializeBeneficiary: (state, action: PayloadAction<Beneficiary>) => {
			const beneficiary = action.payload;

			// Extract chosen category IDs and available category options from beneficiary data
			// chosenCategoryIds: IDs of categories this beneficiary is allowed to use
			// availableCategoryOptions: Full category objects for displaying labels/translations
			let chosenCategoryIds: string[] = [];
			let availableCategoryOptions: PaymentCategoryOption[] = [];

			if (beneficiary.paymentCategories && Array.isArray(beneficiary.paymentCategories)) {
				chosenCategoryIds = beneficiary.paymentCategories.map((cat) => {
					if (typeof cat === 'string') {
						return cat;
					} else {
						// Preserve the full option objects for immediate display
						availableCategoryOptions.push(cat);
						return cat.id;
					}
				});
			}

			// If we have category options from the beneficiary data, use them immediately
			// This prevents showing IDs while waiting for the API call
			if (availableCategoryOptions.length > 0) {
				state.paymentCategoryOptions = availableCategoryOptions;
			}

			state.upsertedBeneficiary = {
				...beneficiary,
				paymentCategories: chosenCategoryIds,
			};

			if (_.has(state.upsertedBeneficiary, 'accountNumber')) {
				_.assign(state.upsertedBeneficiary, {
					[paymentsMethodsMapping.acct_number]: _.get(state.upsertedBeneficiary, 'accountNumber'),
				});
			}
			state.activeStep = beneficiarySteps.BENEFICIARY;
		},
		setSteps: (state, action: PayloadAction<Partial<steps>>) => {
			state.steps = { ...state.steps, ...action.payload };
		},
		setActiveStep: (state, action: PayloadAction<beneficiarySteps>) => {
			state.activeStep = action.payload;
		},
		setBeneficiaries: (state, action: PayloadAction<Beneficiary[]>) => {
			const modifiedBeneficiaries = action.payload.map((item: any) => {
				const acct_number = _.isNil(item?.accountNumber) ? {} : { acctNumber: item?.accountNumber };

				// Transform allowedPaymentCategories tree (from API) to paymentCategories flat array
				// Keep both: allowedPaymentCategories (tree for details page) and paymentCategories (flat for payment form)
				const paymentCategories = transformBeneficiaryCategories(item);
				const allowedPaymentCategories = item.allowedPaymentCategories || [];

				return { ...item, ...acct_number, paymentCategories, allowedPaymentCategories };
			});
			state.items = modifiedBeneficiaries;
		},
		upsertBeneficiary: (state, action: PayloadAction<Partial<Beneficiary>>) => {
			state.upsertedBeneficiary = { ...state.upsertedBeneficiary, ...action.payload };
		},
		setBeneficiary: (state, action: PayloadAction<formValue | any>) => {
			state.upsertedBeneficiary = {
				...state.upsertedBeneficiary,
				[action.payload.name]: action.payload.value,
			};
		},
		setBeneficiaryStatus: (state, action: PayloadAction<{ beneficiary: Beneficiary }>) => {
			const findIndex = _.findIndex(state.items, ['id', action.payload.beneficiary.id]);
			if (findIndex > -1) {
				state.items[findIndex].status = action.payload.beneficiary.status;
				state.items[findIndex] = { ...state.items[findIndex], ...action.payload.beneficiary };
			}
		},
		deleteBeneficiaryById: (state, action: PayloadAction<number>) => {
			const findIndex = _.findIndex(state.items, ['id', action.payload]);
			if (findIndex > -1) {
				_.pullAt(state.items, [findIndex]);
			}
		},
		setOptionalPayments: (state) => {
			let list: paymentsMethodType = {};
			const payments = state.upsertedBeneficiary.payments;
			const ifOptional = ifPaymentOptional(state, true);
			try {
				{
					_.keys(payments).forEach((type) => {
						payments[type].forEach((pay, index) => {
							list[String(type + index)] = {
								optional: ifOptional ? false : true,
								type: type,
								fields: _.intersection(_.keys(pay), paymentsMethodsList),
							};
						});
					});
				}
			} catch (e) {
				console.log('error', e);
			}
			state.upsertedBeneficiary.paymentsOptions = list;
		},
		setBeneficiaryPayments: (state, action: PayloadAction<formValue>) => {
			state.upsertedBeneficiary = {
				...state.upsertedBeneficiary,
				[action.payload.name]: action.payload.value,
			};
		},
		setPaymentCategoryOptions: (state, action: PayloadAction<PaymentCategoryOption[]>) => {
			state.paymentCategoryOptions = action.payload;
		},
	},
});

export const {
	setLoading,
	setMessage,
	setSteps,
	setActiveStep,
	toggleUpserting,
	intializeBeneficiary,
	setBeneficiary,
	upsertBeneficiary,
	setBeneficiaries,
	setOptionalPayments,
	setLoadingBeneficiaries,
	setStartLoadingBeneficiaries,
	setBeneficiaryStatus,
	deleteBeneficiaryById,
	setPaymentCategoryOptions,
} = beneficiarySlice.actions;

export const isLoading = (state: any) => state.beneficiary.loading;
export const getMessage = (state: any) => state.beneficiary.message;
export const isCreatingProcess = (state: any) => state.beneficiary.upsertingProcess;
export const getNewBeneficiary = (state: any) => state.beneficiary.upsertedBeneficiary;
export const getSteps = (state: any) => state.beneficiary.steps;
const beneficiarySelector = (state) => state.beneficiary;
export const getActiveStep = (state: any) => {
	return _.indexOf(_.values(beneficiarySteps), state.beneficiary.activeStep);
};
export const getBeneficiaryDetails = createSelector([beneficiarySelector], (state: any): obj => {
	let obj: obj = {};
	let bn = state.upsertedBeneficiary;
	if (bn.type === beneficiaryType.COMPANY) {
		BeneficiaryDetailsCompany.forEach((key) => (obj[key] = bn[key] ? String(bn[key]) : ''));
	}
	if (bn.type === beneficiaryType.INDIVIDUAL) {
		BeneficiaryDetailsLocal.forEach((key) => (obj[key] = bn[key] ? String(bn[key]) : ''));
	}
	return obj;
});

export const getBeneficiaryDetailsCompanyForDisplay = createSelector([beneficiarySelector], (state: any): obj => {
	let obj: obj = {};
	let bn = state.upsertedBeneficiary;
	if (bn.type === beneficiaryType.COMPANY) {
		BeneficiaryDetailsCompanyForDisplay.forEach((key) => (obj[key] = bn[key] ? String(bn[key]) : ''));
	}
	if (bn.type === beneficiaryType.INDIVIDUAL) {
		BeneficiaryDetailsLocal.forEach((key) => (obj[key] = bn[key] ? String(bn[key]) : ''));
	}
	return obj;
});

export const getPaymentCategoryOptions = (state: any) => state.beneficiary.paymentCategoryOptions;

export const getBeneficiaryBankDetails = (state: any): obj => {
	let obj: obj = {};
	let bn = state.beneficiary.upsertedBeneficiary;
	BeneficiaryDetailsBankAccount.forEach((key) => {
		if (bn[key] !== null && typeof bn[key] !== 'undefined') {
			obj[key] = String(bn[key]);
		}
	});

	return obj;
};

export const getBeneficiaryLocalPayment = (state: any) => {
	let bn = state.beneficiary.upsertedBeneficiary;
	return {
		iban: bn?.iban,
	};
};

export const getBeneficiaryPayments = (state: any) => {
	let bn = state.beneficiary.upsertedBeneficiary;
	let obj = {};

	_.keys(paymentsMethodsMapping).forEach((key) => {
		if (
			!_.isNil(_.get(bn, paymentsMethodsMapping[key])) &&
			_.includes(paymentsMethodsDisplayList, paymentsMethodsMapping[key])
		) {
			obj[key] = _.get(bn, paymentsMethodsMapping[key]);
		}
	});
	return obj;
};
export const getBeneficiaryPaymentsProfilePage = (state: any) => {
	let bn = state.beneficiary.upsertedBeneficiary;
	let obj = {};

	_.keys(paymentsMethodsBeneficiaryProfile).forEach((key) => {
		if (
			!_.isNil(_.get(bn, key)) &&
			_.includes(paymentsMethodsDisplayList, _.camelCase(paymentsMethodsBeneficiaryProfile[key]))
		) {
			obj[paymentsMethodsBeneficiaryProfile[key]] = _.get(bn, key);
		}
	});
	return obj;
};

export const getBeneficiaryPaymentsStatus = createSelector([beneficiarySelector], (state: any) => {
	let bn = state.upsertedBeneficiary;
	return {
		isLocalPaymentAllowed: bn.isLocalPaymentAllowed,
		isPriorityPaymentAllowed: bn.isPriorityPaymentAllowed,
	};
});

export const getPaymentOptions = createSelector([beneficiarySelector], (state: any) => {
	return state.upsertedBeneficiary.paymentsOptions;
});

export const ifPaymentOptional = (state: any, local: boolean = false) => {
	let counter: number = 0;
	const payments = local ? state.upsertedBeneficiary.payments : state.beneficiary.upsertedBeneficiary.payments;

	try {
		{
			_.keys(payments).map((type) => {
				counter += payments[type].length;
			});
		}
	} catch (e) {}
	return counter > 1 ? true : false;
};

export const getBeneficiaryPriorityPayment = createSelector([beneficiarySelector], (state: any) => {
	let bn = state.upsertedBeneficiary;
	return {
		iban: bn?.iban,
		bic: bn?.bic,
	};
});

export const getBeneficiaries = (state: any) => {
	return state.beneficiary.items;
};

export const getLoadingBeneficiaries = createSelector([beneficiarySelector], (state: any) => {
	return {
		loadingBeneficiaries: state.loadingBeneficiaries,
		startLoadingBeneficiaries: state.startLoadingBeneficiaries,
	};
});

export const getBeneficiaryById = (id: string) => (state: any) => {
	return _.find(state.beneficiary.items, { [BeneficiaryId]: Number(id) });
};

export const getNonePendingBeneficiaries = createSelector([beneficiarySelector], (state: any) => {
	return _.filter(state.items, (item) => {
		return !_.includes(PendingBeneficiaryStatus, item.status);
	});
});

export const getApprovedBeneficiaries = createSelector([beneficiarySelector], (state: any) => {
	return _.filter(state.items, (item) => {
		return _.includes([BeneficiaryStatus.ApprovedWithRisk, BeneficiaryStatus.Approved], item.status);
	});
});
export default beneficiarySlice.reducer;
